import { Link, useNavigate } from "react-router-dom";
import { AlertTriangle, ArrowLeft, FileQuestion, RefreshCw } from "lucide-react";
import { ApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";

export interface DetailErrorStateProps {
    /**
     * The React Query error (if any). Used to tell a 404 (record missing) from
     * a 5xx / network failure (something broke). When undefined the state is
     * treated as a plain not-found (the fetch resolved without a record).
     */
    error?: unknown;
    /** Re-run the fetch — wired to the Retry button in the server-error state. */
    onRetry?: () => void;
    /** Heading for the not-found state, e.g. "Customer not found". */
    notFoundTitle?: string;
    /**
     * The failing request's id, when the caller has it (our API error envelope
     * carries `requestId`). Shown as a short reference so whoever hits the
     * failure can read it out — and the same string greps the Render logs,
     * because the short form is the head of the full id. Intentionally NOT the
     * whole UUID: humans shouldn't be handed 36 characters of hex.
     */
    requestId?: string | null;
    /** Optional link to the relevant list screen, shown in the not-found state. */
    listHref?: string;
    listLabel?: string;
}

const CARD = "mx-auto max-w-md rounded-xl border border-border/70 bg-card p-8 text-center shadow-[var(--shadow-card)]";

/**
 * Shared detail-page error/empty state that distinguishes a 404 ("Not found",
 * with a Back button + list link) from a 5xx/network failure ("Something went
 * wrong", with a Retry button). Always renders on an opaque card so the copy
 * stays legible over the blueprint watermark.
 */
export function DetailErrorState({
    error,
    onRetry,
    notFoundTitle = "Not found",
    listHref,
    listLabel = "Back to list",
    requestId,
}: DetailErrorStateProps) {
    const navigate = useNavigate();

    // Anything that isn't a clean 404 is treated as a server-side failure the
    // user can retry. A resolved-but-empty fetch (error undefined) is a 404.
    const isServerError = !!error && !(error instanceof ApiError && error.status === 404);

    if (isServerError) {
        // Our API's own words when it gave any — a rate limit should read as
        // "Too many API requests; try again later.", not as a generic shrug.
        // A gateway/HTML failure has no envelope, so it keeps the plain line.
        const envelopeMessage =
            error instanceof ApiError &&
            typeof error.body === "object" &&
            error.body !== null &&
            typeof (error.body as { message?: unknown }).message === "string"
                ? (error.body as { message: string }).message.trim()
                : "";
        return (
            <div role="alert" className={CARD}>
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
                    <AlertTriangle className="h-6 w-6 text-destructive" />
                </div>
                <h2 className="text-base font-semibold text-foreground">Something went wrong</h2>
                <p className="mt-1 text-sm text-foreground/80">
                    {envelopeMessage || "Please try again or contact support if it keeps happening."}
                </p>
                {requestId && (
                    <p className="mt-2 text-xs text-muted-foreground" title={requestId}>
                        Reference <span className="font-mono">{requestId.slice(0, 8)}</span>
                    </p>
                )}
                {onRetry && (
                    <Button onClick={onRetry} className="mt-4">
                        <RefreshCw className="h-4 w-4" />
                        Retry
                    </Button>
                )}
            </div>
        );
    }

    return (
        <div role="status" className={CARD}>
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                <FileQuestion className="h-6 w-6 text-foreground/70" />
            </div>
            <h2 className="text-base font-semibold text-foreground">{notFoundTitle}</h2>
            <p className="mt-1 text-sm text-foreground/80">It may have been deleted, or the link is out of date.</p>
            <div className="mt-4 flex items-center justify-center gap-2">
                <Button variant="outline" onClick={() => navigate(-1)}>
                    <ArrowLeft className="h-4 w-4" />
                    Back
                </Button>
                {listHref && (
                    <Link to={listHref} className="inline-flex items-center text-sm text-primary hover:underline">
                        {listLabel}
                    </Link>
                )}
            </div>
        </div>
    );
}
